import React, { useState, useRef, useCallback, useMemo } from "react";
import "./dynamicCheckService.css";
import "react-phone-number-input/style.css";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogTitle } from "@mui/material";
import CheckServiceCheckout from "../stripe/CheckServiceCheckout";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLoading } from "../../_shared/LoadingContext";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { createPaymentIntent, addCheckServiceOrder } from "../../_services/checkService";
import cookies from "js-cookie";

// Load Stripe outside the component to avoid reinitializing it on every render
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TEST_KEY);
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_LIVE_KEY);


const SECRET_KEY = "6LcjP2spAAAAAPk0RY2IdFqOEDGz0OwxoXALjjbS";
const SITE_KEY = "6LcjP2spAAAAAGEdDTXPvavTKTSHVq2lo5jItpk8";

const DynamicCheckService = () => {
  const { t } = useTranslation();
  const currentLanguageCode = cookies.get("i18next") || "en";
  const { checkService } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [valid_token, setValidToken] = useState([]);

  const { startLoading, stopLoading } = useLoading();
  const captchaRef = useRef(null);

  // Avoid re-creating functions on every render
  const handleClose = useCallback(() => setOpenDialog(false), []);
  const openDialogBox = useCallback(() => setOpenDialog(true), []);

  // Create memoized data for service-related values
  const serviceData = useMemo(() => {
    switch (checkService) {
      case "icloud-lock-checker":
        return {
          url: "icloud-lock-checker",
          provider: "CellfServices",
          serviceId: 28,
          price: 3.99,
          title: "iPhone FMI Check (Clean/Lost)",
          subtitle: "The Find My iPhone check verifies whether the 'Find My iPhone' feature is enabled. This feature is crucial for locating a lost or stolen device and protecting personal information. If disabled, the device is more vulnerable and recovery becomes more difficult."

        };
      case "premium-blacklist-checker":
        return {
          url: "premium-blacklist-checker",
          provider: "DashServices",
          serviceId: 3,
          price: 3.99,
          title: "Premium blacklist check",
          subtitle: "Verify if your phone is blacklisted or clean using its IMEI number. Ensure your device is not blacklisted due to loss, theft, or unpaid bills."
        };
      case "basic-imei-checker":
        return {
          url: "basic-imei-checker",
          provider: "DashServices",
          serviceId: 0,
          price: 1.99,
          title: "Basic IMEI check",
          subtitle: "Find out only the brand and model of your phone using the IMEI number."
        };
      case "apple-full-info-check-gsx":
        return {
          url: "apple-full-info-check-gsx",
          provider: "DashServices",
          serviceId: 11,
          price: 1.99,
          title: "Apple full info check (GSX Report)",
          subtitle: "The iPhone GSX (Global Service Exchange) Report provides detailed information about an iPhone's status and history, including warranty details, repairs, and technical issues. It helps verify device authenticity and assess its condition for repairs or purchases."
        };
        case "apple-warranty-check":
        return {
          url: "apple-warranty-check",
          provider: "DashServices",
          serviceId: 12,
          price: 1.99,
          title: "iPhone warrantly check",
          subtitle: "Verifying an iPhone's warranty is crucial to determine if it is covered by Apple's support and repair services. This is important to avoid additional repair costs and to ensure the device's condition when buying it second-hand."
        };
        case "esim-info-check":
        return {
          url: "esim-info-check",
          provider: "DashServices",
          serviceId: 52,
          price: 1.99,
          title: "eSIM info check",
          subtitle: "Check if your phone is compatible with eSIM technology."
        };
        case "carrier-lock-status":
        return {
          url: "esim-info-check",
          provider: "DashServices",
          serviceId: 2,
          price: 1.99,
          title: "eSIM info check",
          subtitle: "Check if your phone is compatible with eSIM technology."
        };
      // Add more cases as needed
      default:
        return {
          url: "default-service",
          title: "Default Service",
          subtitle: "This is a default service description.",
        };
    }
  }, [checkService]);

  // Form validation schema
  const validationSchema = Yup.object({
    imei: Yup.string()
      .required(t("ErrorField4"))
      .length(15, t("ErrorField41")),
    email: Yup.string().email(t("ErrorField51")).required(t("ErrorField5")),
  });

  // Submit handler
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const token = captchaRef.current.getValue();
        captchaRef.current.reset();
        if (!token) {
          return toast.error(t("Sorry!! Verify you're not a bot"));
        }

        startLoading();
        const valid_token = await verifyToken(token);
        setValidToken(valid_token);

        if (valid_token[0]?.success) {
          const body = {
            serviceId: serviceData.serviceId,
            provider: serviceData.provider,
            price: serviceData.price,
            languageId: currentLanguageCode,
            name: serviceData.title,
            email: values.email,
            imei: values.imei,
          };

          const res = await createPaymentIntent(body);
          if (res?.clientSecret) {
            setClientSecret(res.clientSecret);
          } else {
            console.error("Unexpected response format:", res);
          }
        } else {
          toast.error("Verification failed. Please try again.");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        toast.error("Something went wrong");
      } finally {
        stopLoading();
      }
    },
    [serviceData, currentLanguageCode, startLoading, stopLoading, t]
  );

  // Verify reCAPTCHA token
  const verifyToken = useCallback(async (token) => {
    try {
      const response = await Axios.post(`${process.env.REACT_APP_API_URL}/verifyToken`, {
        reCAPTCHA_TOKEN: token,
        Secret_Key: SECRET_KEY,
      });
      toast.success(t("IEMIS1"));
      return [response.data];
    } catch (error) {
      console.error("Error verifying token:", error);
      return [];
    }
  }, [t]);

  return (
    <div>
      <section className="banner-ImeiBlacklist py-md-2 pt-3 pt-md-5 pb-0 position-relative px-2">
        <div className="container py-2 pt-md-4">
          <div className="row justify-content-center p-0 p-md-3">
            <div className="col-md-6 col-12 px-md-5">
              <h1 className="fw-bold display-5 mt-md-5">{serviceData.title}</h1>
              <p className="imeiblacklist text-justify m-0">{serviceData.subtitle}</p>
            </div>
            <div className="col-12 col-md-6 mt-4 mt-md-0">
              {!clientSecret && (
                <>
                  <Formik
                    initialValues={{ imei: "", email: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ handleChange }) => (
                      <Form className="bg-white rounded p-1 p-md-4">
                        <div className="card unlock-card bg-transparent border-0 my-md-0 my-1">
                          <div className="card-body">
                            <h4 className="fw-semibold mb-4">Fill up the form</h4>
                            <div>
                              <p className="mb-0 fw-semibold">IMEI (15 digits)</p>
                              <div className="input-group border d-flex align-items-center p-2 rounded mb-3 bg-white w-100">
                                <label className="input-group-text bg-transparent border-0" htmlFor="imei">
                                  <img loading="lazy" src="/images/ph_phone-call (1).svg" alt="" />
                                  <span className="mx-2mx-0 text-secondary"> | </span>
                                </label>
                                <Field
                                  type="text"
                                  className="form-control border-0 py-1"
                                  placeholder={t("IEMIPC1")}
                                  aria-label={t("IEMIPC1")}
                                  id="imei"
                                  name="imei"
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                            <ErrorMessage name="imei" component="div" className="error-message ps-5 ms-4" />
                            <div>
                              <p className="mb-0 fw-semibold">Email</p>
                              <div className="input-group border d-flex align-items-center p-2 rounded mb-3 bg-white w-100">
                                <label className="input-group-text bg-transparent border-0" htmlFor="email">
                                  <img loading="lazy" src="/images/at-sign2.svg" alt="" />
                                  <span className="mx-2mx-0 text-secondary"> | </span>
                                </label>
                                <Field
                                  type="email"
                                  className="form-control border-0 py-1"
                                  placeholder={t("IEMIPC1")}
                                  aria-label="email"
                                  id="email"
                                  name="email"
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                            <ErrorMessage name="email" component="div" className="error-message ps-5 ms-4" />
                            <button type="submit" className="btn btn-lg mt-md-0 mt-2 w-100 unlock-btn">
                              {t("IEMIB1")}
                            </button>
                            <Link to={`#`} className="btn btn-lg mt-md-3 mt-2 py-3 px-0 w-100 border rounded fs-6">
                             Check Service Free Sample
                            </Link>
                            <div className="row justify-content-center p-0 p-md-3 mt-3 mx-1 bg-light rounded ">
                              <div className="col-12 d-flex justify-content-between p-2 p-md-3 rounded">
                                <h5 className="mb-0">{t('SucH3')}</h5>
                                <h5 className="mb-0">{t('SucH3')}</h5>
                              </div>
                              <div className="col-12 d-flex justify-content-between p-2 p-md-3 rounded">
                                <h5 className="mb-0">{t('SucH4')}</h5>
                                <h5 className="mb-0">{t('SucH3')}</h5>
                              </div>
                              <div className="col-12 d-flex justify-content-between p-2 p-md-3 rounded">
                                <h5 className="mb-0">{t('SucH6')}</h5>
                                <h5 className="mb-0">{t('SucH3')}</h5>
                              </div>
                              
                            </div>
                          </div>

                        </div>
                      </Form>
                    )}
                  </Formik>
                  <div>
                    <ReCAPTCHA className="recaptcha" sitekey={SITE_KEY} ref={captchaRef} />
                  </div>
                </>
              )}
              {clientSecret && (
                <>
                  <p className="imeiblacklist text-justify m-0">You have pay {serviceData?.price}</p>

                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckServiceCheckout formValue={122} customer={3} clientSecret={clientSecret} />
                  </Elements>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DynamicCheckService;
